import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getFieldVisit, 
  createFieldVisitGroup,
  updateFieldVisit, 
  getStaffMembers, 
  getMasterData,
  createFieldTask,
  processVoiceInput,
  saveVoiceCorrection,
  saveVoiceVocabularyBatch,
  suggestVoiceVocabulary
} from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  MapPin,
  Calendar,
  Users,
  Building,
  MessageSquare,
  Package,
  Target,
  Plus,
  X,
  ClipboardList,
  Check,
  ChevronDown,
  ChevronUp,
  Mic,
  MicOff,
  Loader2,
  Play,
  Pause,
  Volume2,
  BookOpen
} from 'lucide-react';
import VoiceVocabularyDialog from '../components/VoiceVocabularyDialog';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';

export default function FieldVisitForm() {
  const navigate = useNavigate();
  const { visitId } = useParams();
  const isEdit = Boolean(visitId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [buyerMarkets, setBuyerMarkets] = useState([]);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Basic visit info
  const [visitInfo, setVisitInfo] = useState({
    visit_date: new Date().toISOString().split('T')[0],
    market_id: '',
    market_name: '',
    staff_participants: []
  });
  const [visitSummary, setVisitSummary] = useState('');
  const [editGroupId, setEditGroupId] = useState(null);

  // Step 2: Buyer-wise details
  const [buyerVisits, setBuyerVisits] = useState({});
  const [expandedBuyer, setExpandedBuyer] = useState(null);

  // Voice recording state
  const [recordingBuyerId, setRecordingBuyerId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [processingVoice, setProcessingVoice] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [originalVoiceData, setOriginalVoiceData] = useState({});
  const [vocabularyDialogOpen, setVocabularyDialogOpen] = useState(false);
  const [vocabularySuggestions, setVocabularySuggestions] = useState([]);
  const [vocabularyReviewMode, setVocabularyReviewMode] = useState(false);
  const [pendingVisitSave, setPendingVisitSave] = useState(false);
  const [checkingVocabulary, setCheckingVocabulary] = useState(false);
  const [savingVocabulary, setSavingVocabulary] = useState(false);
  
  // Audio playback state
  const [playingBuyerId, setPlayingBuyerId] = useState(null);
  const audioPlayerRef = useRef(null);

  // Tracks whether the user has made any edits since load, for Esc-to-go-back guard
  const [formTouched, setFormTouched] = useState(false);
  useUnsavedChanges(formTouched);
  const markTouched = () => setFormTouched(true);

  useEffect(() => {
    loadInitialData();
  }, [visitId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [staffRes, buyersRes, marketsRes] = await Promise.all([
        getStaffMembers(true),
        getMasterData('buyer'),
        getMasterData('buyer_market')
      ]);
      setStaff(staffRes.data);
      setBuyers(buyersRes.data);
      setBuyerMarkets(marketsRes.data);

      if (isEdit) {
        const visitRes = await getFieldVisit(visitId);
        const visit = visitRes.data;
        setEditGroupId(visit.visit_group_id || null);
        
        const market = marketsRes.data.find(m => m.name === visit.geography_market);
        
        setVisitInfo({
          visit_date: visit.visit_date,
          market_id: market?.id || '',
          market_name: visit.geography_market,
          staff_participants: visit.staff_participants || []
        });
        
        setBuyerVisits({
          [visit.buyer_id]: {
            selected: true,
            discussion_notes: visit.discussion_notes || '',
            samples_shown: visit.samples_shown || '',
            potential_items: visit.potential_items || [],
            createTask: false,
            taskData: { description: '', due_date: '', assigned_to: [] }
          }
        });
        
        setCurrentStep(2);
        setExpandedBuyer(visit.buyer_id);
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const buyersInMarket = buyers.filter(b => b.market_id === visitInfo.market_id);

  const handleStaffToggle = (staffId) => {
    markTouched();
    setVisitInfo(prev => ({
      ...prev,
      staff_participants: prev.staff_participants.includes(staffId)
        ? prev.staff_participants.filter(id => id !== staffId)
        : [...prev.staff_participants, staffId]
    }));
  };

  const handleMarketChange = (marketId) => {
    markTouched();
    const market = buyerMarkets.find(m => m.id === marketId);
    setVisitInfo(prev => ({
      ...prev,
      market_id: marketId,
      market_name: market?.name || ''
    }));
    setBuyerVisits({});
  };

  const toggleBuyerSelection = (buyerId) => {
    markTouched();
    setBuyerVisits(prev => {
      const current = prev[buyerId];
      if (current?.selected) {
        const newState = { ...prev };
        delete newState[buyerId];
        return newState;
      } else {
        return {
          ...prev,
          [buyerId]: {
            selected: true,
            discussion_notes: '',
            samples_shown: '',
            potential_items: [],
            createTask: false,
            taskData: { description: '', due_date: '', assigned_to: [] }
          }
        };
      }
    });
  };

  const updateBuyerVisit = (buyerId, field, value) => {
    markTouched();
    setBuyerVisits(prev => ({
      ...prev,
      [buyerId]: {
        ...prev[buyerId],
        [field]: value
      }
    }));
  };

  const addPotentialItem = (buyerId) => {
    markTouched();
    setBuyerVisits(prev => ({
      ...prev,
      [buyerId]: {
        ...prev[buyerId],
        potential_items: [...(prev[buyerId]?.potential_items || []), { item_description: '', notes: '' }]
      }
    }));
  };

  const removePotentialItem = (buyerId, index) => {
    markTouched();
    setBuyerVisits(prev => ({
      ...prev,
      [buyerId]: {
        ...prev[buyerId],
        potential_items: prev[buyerId].potential_items.filter((_, i) => i !== index)
      }
    }));
  };

  const updatePotentialItem = (buyerId, index, field, value) => {
    markTouched();
    setBuyerVisits(prev => ({
      ...prev,
      [buyerId]: {
        ...prev[buyerId],
        potential_items: prev[buyerId].potential_items.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const handleTaskAssigneeToggle = (buyerId, staffId) => {
    markTouched();
    setBuyerVisits(prev => ({
      ...prev,
      [buyerId]: {
        ...prev[buyerId],
        taskData: {
          ...prev[buyerId].taskData,
          assigned_to: prev[buyerId].taskData.assigned_to.includes(staffId)
            ? prev[buyerId].taskData.assigned_to.filter(id => id !== staffId)
            : [...prev[buyerId].taskData.assigned_to, staffId]
        }
      }
    }));
  };

  // Voice Recording Functions - with Safari/iOS compatibility
  const getSupportedMimeType = () => {
    const mimeTypes = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav'];
    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return ''; // Let browser choose default
  };

  const startRecording = async (buyerId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      const actualMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        stream.getTracks().forEach(track => track.stop());
        await processRecording(buyerId, audioBlob);
      };

      mediaRecorder.start();
      setRecordingBuyerId(buyerId);
      setIsRecording(true);
      toast.info('Recording started... Speak now');
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Failed to access microphone. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingBuyerId(null);
    }
  };

  const processRecording = async (buyerId, audioBlob) => {
    const buyer = buyers.find(b => b.id === buyerId);
    if (!buyer) return;

    setProcessingVoice(buyerId);
    try {
      const response = await processVoiceInput(audioBlob, buyer.name);
      const data = response.data;

      // Store original data for correction tracking
      setOriginalVoiceData(prev => ({
        ...prev,
        [buyerId]: {
          transcription: data.transcription,
          analysis: {
            discussion_notes: data.discussion_notes,
            samples_shown: data.samples_shown,
            potential_items: data.potential_items,
            follow_up_task: data.follow_up_task
          },
          audio_url: data.audio_url,
          recording_id: data.recording_id
        }
      }));

      // Populate the form with AI response including audio URL
      setBuyerVisits(prev => ({
        ...prev,
        [buyerId]: {
          ...prev[buyerId],
          discussion_notes: data.discussion_notes || '',
          samples_shown: data.samples_shown || '',
          potential_items: data.potential_items || [],
          audio_url: data.audio_url || null,
          recording_id: data.recording_id || null,
          createTask: !!data.follow_up_task,
          taskData: data.follow_up_task ? {
            description: data.follow_up_task.description || '',
            due_date: '',
            assigned_to: []
          } : { description: '', due_date: '', assigned_to: [] }
        }
      }));

      toast.success('Voice processed! Review and edit the details below.');
    } catch (error) {
      console.error('Voice processing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process voice. Please try again.');
    } finally {
      setProcessingVoice(null);
    }
  };

  // Audio Playback Functions
  const playAudio = (buyerId, audioUrl) => {
    if (playingBuyerId === buyerId && audioPlayerRef.current) {
      // Pause if currently playing
      audioPlayerRef.current.pause();
      setPlayingBuyerId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }

    // Create and play new audio
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const fullUrl = audioUrl.startsWith('http') ? audioUrl : `${backendUrl}${audioUrl}`;
    
    const audio = new Audio(fullUrl);
    audioPlayerRef.current = audio;
    setPlayingBuyerId(buyerId);
    
    audio.onended = () => {
      setPlayingBuyerId(null);
    };
    
    audio.onerror = () => {
      toast.error('Failed to play recording');
      setPlayingBuyerId(null);
    };
    
    audio.play().catch(err => {
      console.error('Audio playback error:', err);
      toast.error('Failed to play recording');
      setPlayingBuyerId(null);
    });
  };

  const goToStep2 = () => {
    if (!visitInfo.visit_date) {
      toast.error('Please select a visit date');
      return;
    }
    if (!visitInfo.market_id) {
      toast.error('Please select a market');
      return;
    }
    if (buyersInMarket.length === 0) {
      toast.error('No buyers found in this market. Please add buyers first.');
      return;
    }
    setCurrentStep(2);
  };

  const goBackToStep1 = () => {
    setCurrentStep(1);
  };

  const getSelectedBuyers = () => {
    return Object.entries(buyerVisits)
      .filter(([_, data]) => data.selected)
      .map(([buyerId, data]) => ({ buyerId, ...data }));
  };

  const saveVisits = async () => {
    const selectedBuyers = getSelectedBuyers();
    
    if (selectedBuyers.length === 0) {
      toast.error('Please select at least one buyer visited');
      return;
    }

    setSaving(true);
    try {
      let destination = '/field-activity';
      for (const buyerData of selectedBuyers) {
        const buyer = buyers.find(b => b.id === buyerData.buyerId);
        
        // Check if data was modified from voice input and save correction
        const originalData = originalVoiceData[buyerData.buyerId];
        if (originalData) {
          const hasChanges = 
            originalData.analysis.discussion_notes !== buyerData.discussion_notes ||
            originalData.analysis.samples_shown !== buyerData.samples_shown ||
            JSON.stringify(originalData.analysis.potential_items) !== JSON.stringify(buyerData.potential_items);
          
          if (hasChanges) {
            try {
              await saveVoiceCorrection({
                original_transcription: originalData.transcription,
                original_analysis: originalData.analysis,
                corrected_data: {
                  discussion_notes: buyerData.discussion_notes,
                  samples_shown: buyerData.samples_shown,
                  potential_items: buyerData.potential_items
                },
                buyer_id: buyerData.buyerId,
                buyer_name: buyer?.name || ''
              });
            } catch (e) {
              console.log('Failed to save correction:', e);
            }
          }
        }

      }

      if (isEdit) {
        const buyerData = selectedBuyers[0];
        const visitResponse = await updateFieldVisit(visitId, {
          visit_date: visitInfo.visit_date,
          geography_market: visitInfo.market_name,
          buyer_id: buyerData.buyerId,
          staff_participants: visitInfo.staff_participants,
          discussion_notes: buyerData.discussion_notes,
          samples_shown: buyerData.samples_shown,
          potential_items: buyerData.potential_items,
          audio_url: buyerData.audio_url || null,
          recording_id: buyerData.recording_id || null,
        });
        if (buyerData.createTask && buyerData.taskData.description.trim() && buyerData.taskData.due_date) {
          await createFieldTask({
            visit_id: visitResponse.data.id,
            buyer_id: buyerData.buyerId,
            description: buyerData.taskData.description,
            due_date: buyerData.taskData.due_date,
            assigned_to: buyerData.taskData.assigned_to,
          });
        }
        if (visitResponse.data.visit_group_id) {
          destination = `/field-activity/group/${visitResponse.data.visit_group_id}`;
        }
        toast.success('Visit updated successfully');
      } else {
        const groupResponse = await createFieldVisitGroup({
          visit_date: visitInfo.visit_date,
          geography_market: visitInfo.market_name,
          staff_participants: visitInfo.staff_participants,
          visit_summary: visitSummary,
          buyers: selectedBuyers.map((buyerData) => ({
            buyer_id: buyerData.buyerId,
            discussion_notes: buyerData.discussion_notes,
            samples_shown: buyerData.samples_shown,
            potential_items: buyerData.potential_items,
            audio_url: buyerData.audio_url || null,
            recording_id: buyerData.recording_id || null,
          })),
        });
        const buyerVisitsByBuyerId = new Map(
          groupResponse.data.buyer_visits.map((visit) => [visit.buyer_id, visit]),
        );
        for (const buyerData of selectedBuyers) {
          const buyerVisit = buyerVisitsByBuyerId.get(buyerData.buyerId);
          if (buyerVisit && buyerData.createTask && buyerData.taskData.description.trim() && buyerData.taskData.due_date) {
            await createFieldTask({
              visit_id: buyerVisit.id,
              buyer_id: buyerData.buyerId,
              description: buyerData.taskData.description,
              due_date: buyerData.taskData.due_date,
              assigned_to: buyerData.taskData.assigned_to,
            });
          }
        }
        destination = `/field-activity/group/${groupResponse.data.id}`;
        toast.success(`Visit ${groupResponse.data.visit_code} recorded with ${selectedBuyers.length} buyer${selectedBuyers.length === 1 ? '' : 's'}`);
      }
      navigate(destination);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save visit(s)');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const getChangedVoicePayloads = () => getSelectedBuyers().flatMap((buyerData) => {
    const originalData = originalVoiceData[buyerData.buyerId];
    if (!originalData) return [];
    const correctedData = {
      discussion_notes: buyerData.discussion_notes,
      samples_shown: buyerData.samples_shown,
      potential_items: buyerData.potential_items,
    };
    const hasChanges =
      originalData.analysis.discussion_notes !== correctedData.discussion_notes
      || originalData.analysis.samples_shown !== correctedData.samples_shown
      || JSON.stringify(originalData.analysis.potential_items) !== JSON.stringify(correctedData.potential_items);
    if (!hasChanges) return [];
    const buyer = buyers.find((item) => item.id === buyerData.buyerId);
    return [{
      original_transcription: originalData.transcription,
      original_analysis: originalData.analysis,
      corrected_data: correctedData,
      buyer_id: buyerData.buyerId,
      buyer_name: buyer?.name || '',
    }];
  });

  const handleSubmit = async () => {
    if (getSelectedBuyers().length === 0) {
      toast.error('Please select at least one buyer visited');
      return;
    }

    const changedVoicePayloads = getChangedVoicePayloads();
    if (changedVoicePayloads.length === 0) {
      await saveVisits();
      return;
    }

    setCheckingVocabulary(true);
    try {
      const responses = await Promise.allSettled(changedVoicePayloads.map(suggestVoiceVocabulary));
      const uniqueSuggestions = [];
      const seenVariants = new Set();
      responses.forEach((response) => {
        if (response.status !== 'fulfilled') return;
        (response.value.data || []).forEach((suggestion) => {
          const key = suggestion.variant.trim().toLowerCase();
          if (!seenVariants.has(key)) {
            seenVariants.add(key);
            uniqueSuggestions.push(suggestion);
          }
        });
      });

      if (uniqueSuggestions.length > 0) {
        setVocabularySuggestions(uniqueSuggestions);
        setVocabularyReviewMode(true);
        setPendingVisitSave(true);
        setVocabularyDialogOpen(true);
        return;
      }
    } catch (error) {
      console.error('Vocabulary suggestion error:', error);
    } finally {
      setCheckingVocabulary(false);
    }

    await saveVisits();
  };

  const openManualVocabulary = () => {
    setVocabularySuggestions([]);
    setVocabularyReviewMode(false);
    setPendingVisitSave(false);
    setVocabularyDialogOpen(true);
  };

  const handleVocabularyConfirm = async (mappings) => {
    setSavingVocabulary(true);
    try {
      if (mappings.length > 0) {
        await saveVoiceVocabularyBatch(mappings);
        toast.success(`${mappings.length} VOHK term${mappings.length === 1 ? '' : 's'} remembered`);
      }
      const shouldSaveVisit = pendingVisitSave;
      setVocabularyDialogOpen(false);
      setPendingVisitSave(false);
      if (shouldSaveVisit) await saveVisits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vocabulary memory');
    } finally {
      setSavingVocabulary(false);
    }
  };

  const handleVocabularySkip = async () => {
    const shouldSaveVisit = pendingVisitSave;
    setVocabularyDialogOpen(false);
    setPendingVisitSave(false);
    if (shouldSaveVisit) await saveVisits();
  };

  const handleVocabularyOpenChange = (open) => {
    setVocabularyDialogOpen(open);
    if (!open) {
      setPendingVisitSave(false);
      setVocabularyReviewMode(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="field-visit-form">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/field-activity')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-maroon-900">
            {isEdit ? 'Edit Visit' : 'Record New Visit'}
          </h1>
          <p className="text-sm text-gray-500">
            Step {currentStep} of 2: {currentStep === 1 ? 'Visit Details' : 'Buyer Information'}
          </p>
        </div>
        {/* Step indicator */}
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep >= 1 ? 'bg-maroon-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            {currentStep > 1 ? <Check className="h-4 w-4" /> : '1'}
          </div>
          <div className="w-8 h-1 bg-gray-200">
            <div className={`h-full transition-all ${currentStep >= 2 ? 'bg-maroon-600 w-full' : 'w-0'}`}></div>
          </div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep >= 2 ? 'bg-maroon-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            2
          </div>
        </div>
      </div>

      {/* Step 1: Visit Details */}
      {currentStep === 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <MapPin className="h-4 w-4 text-maroon-600" />
              Visit Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Visit Date */}
            <div>
              <Label htmlFor="visit_date" className="flex items-center gap-1 mb-1">
                <Calendar className="h-3 w-3" />
                Visit Date *
              </Label>
              <Input
                id="visit_date"
                type="date"
                value={visitInfo.visit_date}
                onChange={(e) => { markTouched(); setVisitInfo(prev => ({ ...prev, visit_date: e.target.value })); }}
                data-testid="visit-date-input"
              />
            </div>

            {/* Market Selection */}
            <div>
              <Label htmlFor="market" className="flex items-center gap-1 mb-1">
                <MapPin className="h-3 w-3" />
                Market *
              </Label>
              <MasterDataAutocomplete
                value={visitInfo.market_id}
                onValueChange={handleMarketChange}
                options={buyerMarkets.map((market) => {
                  const buyerCount = buyers.filter((buyer) => buyer.market_id === market.id).length;
                  return { value: market.id, label: market.name, description: `${buyerCount} buyers` };
                })}
                placeholder="Select a market"
                searchPlaceholder="Search markets..."
                emptyMessage="No matching market in Master Data"
                testId="market-select"
              />
              {buyerMarkets.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No markets found. Add buyer markets in Master Data first.
                </p>
              )}
            </div>

            {/* Staff Participants */}
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <Users className="h-3 w-3" />
                Staff Participants
              </Label>
              <div className="flex flex-wrap gap-2">
                {staff.map((member) => (
                  <label
                    key={member.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      visitInfo.staff_participants.includes(member.id)
                        ? 'bg-maroon-100 border-maroon-500 text-maroon-800'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <Checkbox
                      checked={visitInfo.staff_participants.includes(member.id)}
                      onCheckedChange={() => handleStaffToggle(member.id)}
                      data-testid={`staff-${member.id}`}
                    />
                    <span className="text-sm">{member.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Next Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={goToStep2}
                className="bg-maroon-600 hover:bg-maroon-700"
                data-testid="next-step-btn"
              >
                Next: Select Buyers
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Buyer Selection & Details */}
      {currentStep === 2 && (
        <div className="space-y-4">
          {/* Summary of Step 1 */}
          <Card className="bg-maroon-50 border-maroon-200">
            <CardContent className="p-3 lg:p-2">
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-maroon-600" />
                    {new Date(visitInfo.visit_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-maroon-600" />
                    {visitInfo.market_name}
                  </span>
                  {visitInfo.staff_participants.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-maroon-600" />
                      {staff.filter(s => visitInfo.staff_participants.includes(s.id)).map(s => s.name).join(', ')}
                    </span>
                  )}
                </div>
                {!isEdit && (
                  <Button variant="ghost" size="sm" onClick={goBackToStep1} data-testid="edit-visit-info-button">
                    Edit
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {!isEdit && (
            <Card className="border-maroon-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-maroon-600" />
                  Overall Visit Summary
                </CardTitle>
                <p className="text-xs text-gray-500">One shared summary for this complete market visit. Buyer-specific details remain below.</p>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={visitSummary}
                  onChange={(event) => { markTouched(); setVisitSummary(event.target.value); }}
                  rows={4}
                  placeholder="Overall outcome, common observations, next steps across the full visit..."
                  data-testid="overall-visit-summary-input"
                />
              </CardContent>
            </Card>
          )}

          {/* Buyers List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base lg:text-lg flex items-center gap-2">
                <Building className="h-4 w-4 text-maroon-600" />
                Buyers in {visitInfo.market_name} ({buyersInMarket.length})
              </CardTitle>
              <p className="text-xs text-gray-500">Select buyers and use the mic to record visit details in Hindi/English/Hinglish</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {buyersInMarket.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No buyers in this market</p>
                </div>
              ) : (
                buyersInMarket.map((buyer) => {
                  const isSelected = Boolean(buyerVisits[buyer.id]?.selected);
                  const isExpanded = expandedBuyer === buyer.id;
                  const buyerData = buyerVisits[buyer.id] || {};
                  const isProcessing = processingVoice === buyer.id;
                  const isRecordingThis = recordingBuyerId === buyer.id && isRecording;

                  return (
                    <div
                      key={buyer.id}
                      className={`border rounded-lg transition-all ${
                        isSelected ? 'border-maroon-400 bg-maroon-50' : 'border-gray-200'
                      }`}
                    >
                      {/* Buyer Header */}
                      <div className="p-3 flex items-center justify-between">
                        <div
                          className="flex items-center gap-3 flex-1 cursor-pointer"
                          onClick={() => {
                            if (!isSelected) {
                              toggleBuyerSelection(buyer.id);
                              setExpandedBuyer(buyer.id);
                            } else {
                              setExpandedBuyer(isExpanded ? null : buyer.id);
                            }
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleBuyerSelection(buyer.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`buyer-checkbox-${buyer.id}`}
                          />
                          <div>
                            <span className="font-medium text-maroon-900">{buyer.name}</span>
                            {isSelected && buyerData.discussion_notes && (
                              <p className="text-xs text-gray-500 truncate max-w-[200px]">
                                {buyerData.discussion_notes}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Mic Button */}
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            {isProcessing ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled
                                className="desktop-mic-button text-maroon-600 md:h-11 md:w-11 md:p-0"
                                data-testid={`mic-processing-${buyer.id}`}
                              >
                                <Loader2 className="h-5 w-5 md:h-[26px] md:w-[26px] animate-spin" strokeWidth={2.8} />
                              </Button>
                            ) : isRecordingThis ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  stopRecording();
                                }}
                                className="desktop-mic-button text-red-600 hover:bg-red-50 animate-pulse md:h-11 md:w-11 md:p-0"
                                data-testid={`mic-stop-${buyer.id}`}
                              >
                                <MicOff className="h-5 w-5 md:h-[26px] md:w-[26px]" strokeWidth={2.8} />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startRecording(buyer.id);
                                }}
                                className="desktop-mic-button text-maroon-600 hover:bg-maroon-100 md:h-11 md:w-11 md:p-0"
                                title="Record voice in Hindi/English/Hinglish"
                                data-testid={`mic-start-${buyer.id}`}
                              >
                                <Mic className="h-5 w-5 md:h-[26px] md:w-[26px]" strokeWidth={2.8} />
                              </Button>
                            )}
                            {/* Play Recording Button - shows when audio_url exists */}
                            {buyerData.audio_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  playAudio(buyer.id, buyerData.audio_url);
                                }}
                                className={playingBuyerId === buyer.id ? "text-green-600 hover:bg-green-50" : "text-blue-600 hover:bg-blue-50"}
                                title={playingBuyerId === buyer.id ? "Pause recording" : "Play recording"}
                                data-testid={`play-audio-${buyer.id}`}
                              >
                                {playingBuyerId === buyer.id ? (
                                  <Pause className="h-5 w-5" />
                                ) : (
                                  <Play className="h-5 w-5" />
                                )}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setExpandedBuyer(isExpanded ? null : buyer.id)}
                              data-testid={`buyer-expand-${buyer.id}`}
                            >
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Expanded Details */}
                      {isSelected && isExpanded && (
                        <div className="px-3 pb-3 space-y-3 border-t border-maroon-200 pt-3">
                          {/* Audio Player - shows when recording exists */}
                          {buyerData.audio_url && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 flex items-center gap-2">
                              <Volume2 className="h-4 w-4 text-blue-600" />
                              <span className="text-xs text-blue-700 flex-1">Voice recording saved</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => playAudio(buyer.id, buyerData.audio_url)}
                                className="h-7 text-xs"
                              >
                                {playingBuyerId === buyer.id ? (
                                  <>
                                    <Pause className="h-3 w-3 mr-1" />
                                    Pause
                                  </>
                                ) : (
                                  <>
                                    <Play className="h-3 w-3 mr-1" />
                                    Play
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                          
                          {/* Voice hint */}
                          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded flex items-center gap-2">
                            <Mic className="h-3 w-3 shrink-0" />
                            <span className="flex-1">Tap the mic icon to record in Hindi/English/Hinglish. AI will auto-fill the fields below.</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={openManualVocabulary}
                              className="h-7 text-xs shrink-0"
                              data-testid={`open-vocabulary-${buyer.id}`}
                            >
                              <BookOpen className="h-3.5 w-3.5 mr-1" />
                              Remember term
                            </Button>
                          </div>

                          {/* Discussion Notes */}
                          <div>
                            <Label className="text-xs flex items-center gap-1 mb-1">
                              <MessageSquare className="h-3 w-3" />
                              Discussion Notes
                            </Label>
                            <Textarea
                              placeholder="Key points discussed..."
                              value={buyerData.discussion_notes || ''}
                              onChange={(e) => updateBuyerVisit(buyer.id, 'discussion_notes', e.target.value)}
                              rows={2}
                              className="text-sm"
                              data-testid={`discussion-${buyer.id}`}
                            />
                          </div>

                          {/* Samples Shown */}
                          <div>
                            <Label className="text-xs flex items-center gap-1 mb-1">
                              <Package className="h-3 w-3" />
                              Samples/Items Shown
                            </Label>
                            <Textarea
                              placeholder="List of samples shown..."
                              value={buyerData.samples_shown || ''}
                              onChange={(e) => updateBuyerVisit(buyer.id, 'samples_shown', e.target.value)}
                              rows={2}
                              className="text-sm"
                              data-testid={`samples-${buyer.id}`}
                            />
                          </div>

                          {/* Potential Items */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-xs flex items-center gap-1">
                                <Target className="h-3 w-3" />
                                Potential Items
                              </Label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => addPotentialItem(buyer.id)}
                                className="h-6 text-xs"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add
                              </Button>
                            </div>
                            {(buyerData.potential_items || []).map((item, index) => (
                              <div key={index} className="flex gap-2 items-start mb-2">
                                <div className="flex-1 space-y-1">
                                  <Input
                                    placeholder="Item description"
                                    value={item.item_description}
                                    onChange={(e) => updatePotentialItem(buyer.id, index, 'item_description', e.target.value)}
                                    className="text-sm h-8"
                                  />
                                  <Input
                                    placeholder="Notes (optional)"
                                    value={item.notes}
                                    onChange={(e) => updatePotentialItem(buyer.id, index, 'notes', e.target.value)}
                                    className="text-sm h-8"
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removePotentialItem(buyer.id, index)}
                                  className="h-8 text-red-600"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>

                          {/* Follow-up Task */}
                          {!isEdit && (
                            <div className="pt-2 border-t">
                              <div className="flex items-center gap-2 mb-2">
                                <Checkbox
                                  id={`task-${buyer.id}`}
                                  checked={buyerData.createTask || false}
                                  onCheckedChange={(checked) => updateBuyerVisit(buyer.id, 'createTask', checked)}
                                />
                                <Label htmlFor={`task-${buyer.id}`} className="text-xs flex items-center gap-1 cursor-pointer">
                                  <ClipboardList className="h-3 w-3" />
                                  Create Follow-up Task
                                </Label>
                              </div>
                              {buyerData.createTask && (
                                <div className="space-y-2 ml-6">
                                  <Input
                                    placeholder="Task description"
                                    value={buyerData.taskData?.description || ''}
                                    onChange={(e) => updateBuyerVisit(buyer.id, 'taskData', { ...buyerData.taskData, description: e.target.value })}
                                    className="text-sm h-8"
                                  />
                                  <Input
                                    type="date"
                                    value={buyerData.taskData?.due_date || ''}
                                    onChange={(e) => updateBuyerVisit(buyer.id, 'taskData', { ...buyerData.taskData, due_date: e.target.value })}
                                    className="text-sm h-8"
                                  />
                                  <div className="flex flex-wrap gap-1">
                                    {staff.map((member) => (
                                      <label
                                        key={member.id}
                                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs border cursor-pointer ${
                                          buyerData.taskData?.assigned_to?.includes(member.id)
                                            ? 'bg-blue-100 border-blue-400'
                                            : 'bg-gray-50 border-gray-200'
                                        }`}
                                      >
                                        <Checkbox
                                          checked={buyerData.taskData?.assigned_to?.includes(member.id) || false}
                                          onCheckedChange={() => handleTaskAssigneeToggle(buyer.id, member.id)}
                                          className="h-3 w-3"
                                        />
                                        {member.name}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between gap-3 pt-2">
            {!isEdit ? (
              <Button
                type="button"
                variant="outline"
                onClick={goBackToStep1}
                disabled={saving}
                data-testid="back-to-visit-info-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(editGroupId ? `/field-activity/group/${editGroupId}` : '/field-activity')}
                disabled={saving}
                data-testid="cancel-visit-form-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-maroon-600 hover:bg-maroon-700"
                disabled={saving || checkingVocabulary || getSelectedBuyers().length === 0}
                data-testid="save-visit-btn"
              >
                {saving || checkingVocabulary ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {checkingVocabulary ? 'Checking terms...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isEdit ? 'Update Visit' : `Save ${getSelectedBuyers().length} Visit${getSelectedBuyers().length > 1 ? 's' : ''}`}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      <VoiceVocabularyDialog
        open={vocabularyDialogOpen}
        onOpenChange={handleVocabularyOpenChange}
        suggestions={vocabularySuggestions}
        reviewMode={vocabularyReviewMode}
        saving={savingVocabulary}
        onConfirm={handleVocabularyConfirm}
        onSkip={handleVocabularySkip}
      />
    </div>
  );
}
